import { useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { establishSession } from "./api/session.js";
import { ApiError } from "./api/client.js";
import { useSessionExpired } from "./api/sessionState.js";
import { AccessDenied } from "./components/AccessDenied.js";
import { ReloadPrompt } from "./components/ReloadPrompt.js";
import { OrgPickerPage } from "./routes/OrgPickerPage.js";
import { AdListPage } from "./routes/AdListPage.js";
import { AdFormPage } from "./routes/AdFormPage.js";

type State =
  | { status: "loading" }
  | { status: "ready" }
  | { status: "denied" } // US-A2: utype != 1 (403)
  | { status: "error"; message: string };

/**
 * App shell (US-A1/A2/A3). Bootstraps the iframe session, then renders the router. A `403`
 * at handshake → AccessDenied; any other failure → a generic error screen; a mid-session
 * `401` that outlives a refresh → the ReloadPrompt overlay (driven by the session store).
 */
export function App() {
  const [state, setState] = useState<State>({ status: "loading" });
  const expired = useSessionExpired();

  useEffect(() => {
    establishSession()
      .then(() => setState({ status: "ready" }))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 403) setState({ status: "denied" });
        else setState({ status: "error", message: err instanceof Error ? err.message : String(err) });
      });
  }, []);

  if (state.status === "loading") {
    return (
      <main className="state-screen">
        <p>Authenticating…</p>
      </main>
    );
  }
  if (state.status === "denied") return <AccessDenied />;
  if (state.status === "error") {
    return (
      <main className="state-screen" role="alert">
        <div className="state-card">
          <h1>Could not start</h1>
          <p>{state.message}</p>
        </div>
      </main>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/" element={<OrgPickerPage />} />
        <Route path="/orgs/:orgId/ads" element={<AdListPage />} />
        <Route path="/orgs/:orgId/ads/new" element={<AdFormPage mode="create" />} />
        <Route path="/ads/:id/edit" element={<AdFormPage mode="edit" />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {expired && <ReloadPrompt />}
    </>
  );
}
